import { FC, useState } from 'react';
import { VoidFn } from 'types/baseTypes';
import { CircleCloseIcon, DeleteIcon } from 'assets/icons';
import { JobLeadSourceTypes, LeadSource } from '../constant';
import { CustomButton, CustomInput, CustomModal, RadioWithLabel } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';

interface EditLeadSourceModalProps {
  open: boolean;
  onClose: VoidFn;
  jobNumber: string;
  leadSource: string;
  leadSourceType: string;
  onSaveClick: (leadSourceType: string, leadSource: string) => Promise<void>;
  isActionInProgress: boolean;
  onRemoveClick: VoidFn;
  isRemoveLoading: boolean;
}

const EditLeadSourceModal: FC<EditLeadSourceModalProps> = ({
  open,
  onClose,
  jobNumber,
  leadSource,
  leadSourceType,
  onSaveClick,
  isActionInProgress,
  onRemoveClick,
  isRemoveLoading
}) => {
  const [jobLeadSource, setJobLeadSource] = useState(leadSource);
  const [jobLeadSourceType, setJobLeadSourceType] = useState(leadSourceType);
  const hasLeadSource = jobLeadSourceType === LeadSource.REFERRAL || jobLeadSourceType === LeadSource.OTHER;

  const onLeadSourceChange = (sourceType: string) => {
    if (sourceType === leadSourceType) {
      setJobLeadSource(leadSource);
    } else setJobLeadSource('');
    setJobLeadSourceType(sourceType);
  };

  const isSaveDisabled = () =>
    (jobLeadSource.trim() === leadSource.trim() && leadSourceType === jobLeadSourceType) ||
    (hasLeadSource ? jobLeadSource.trim() === '' : false);

  return (
    <CustomModal open={open} closeModal={onClose} backgroundColor="bg-white" width="md:w-[510px]">
      <div className="flex items-center justify-between px-[30px] py-6">
        <div className="relative w-full">
          <h2 className="text-2xl font-semibold text-headingGray">Edit lead source</h2>
          <p className="text-gray-600"> {jobNumber}</p>
          <CircleCloseIcon className="absolute right-0 top-0 cursor-pointer" onClick={onClose} />
        </div>
      </div>
      <div className="mx-[30px] space-y-4 divide-y rounded-md border border-borderGray px-3 pb-4">
        {JobLeadSourceTypes.map(sourceType => (
          <div key={sourceType.value} className="pt-4">
            <RadioWithLabel
              isSelected={sourceType.value === jobLeadSourceType}
              label={sourceType.label}
              onClick={() => onLeadSourceChange(sourceType.value)}
              labelStyle={{ fontSize: '16px' }}
            />
            {sourceType.hasSource && sourceType.value === jobLeadSourceType && (
              <CustomInput
                id="edit-lead-source-type-input"
                value={jobLeadSource}
                className="mt-2 ml-6 w-full rounded-md border border-borderGray bg-gray-50 p-3 outline-none"
                onChange={setJobLeadSource}
                placeholder={sourceType.inputPlaceholder}
              />
            )}
          </div>
        ))}
      </div>

      <div className="mx-[30px] my-6 flex items-center justify-between">
        {leadSourceType && (
          <CustomButton
            isLoading={isRemoveLoading}
            id="edit-lead-source-remove-button"
            onClick={onRemoveClick}
            type={ButtonType.GHOST}
            className="flex cursor-pointer items-center gap-2 px-0"
            StartIcon={DeleteIcon}>
            <span className="text-17px font-semibold text-error">Remove</span>
          </CustomButton>
        )}
        <div className="ml-auto flex items-center gap-3">
          <CustomButton
            id="edit-lead-source-cancel-button"
            onClick={onClose}
            type={ButtonType.SECONDARY}
            className="w-[100px]">
            Cancel
          </CustomButton>
          <CustomButton
            id="edit-lead-source-save-button"
            onClick={() => onSaveClick(jobLeadSourceType, jobLeadSource.trim())}
            disabled={isSaveDisabled()}
            isLoading={isActionInProgress && !isRemoveLoading}
            className="w-[100px]">
            Save
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default EditLeadSourceModal;
